import React from 'react';

import {bulkArchiveRequestTaskSchemas, bulkUnarchiveRequestTaskSchemas} from 'archive/ArchiveActions';
import DataConnector from 'connect/DataConnector';
import {getStore} from 'redux-stores/Store';
import useRequestTaskSchemaQuery from 'requests/graphql/useRequestTaskSchemaQuery';
import * as RequestTaskSchemaDAO from 'requests/schemas/RequestTaskSchemaDAO';
import * as RequestTaskSchemaReduxActions from 'requests/schemas/RequestTaskSchemaReduxActions';
import {isRequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {
  EnhancedProps,
  SchemaEditSection,
  SchemaPage,
  SchemaPageEditSectionProps,
  SchemaPageProps,
} from 'results/assay-schemas/SchemaPage';
import {ArchiveSchemaConfig} from 'schemas/SchemaSettingsPageContainer';
import {SchemaSpec} from 'schemas/spec/SchemaConfigConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';

function getEditingName(stateName: string | null, schema: RequestTaskSchemaGraphQL | null): string {
  if (stateName != null) {
    return stateName;
  } else if (schema) {
    return schema.name;
  } else {
    return '';
  }
}

class RequestTaskSchemaEditConnector extends DataConnector<
  {schemaId: string; schema?: AssaySchema | RequestTaskSchemaGraphQL | null},
  EnhancedProps
> {
  // Ideally could re-use this across schema connectors. Find a better way to re-use code in this class next
  // time a specced model is added

  enhanceProps(): EnhancedProps {
    const requestTaskSchema = isRequestTaskSchema(this.props.schema) ? this.props.schema : null;

    const fetchSchema = (schemaId: string): void => {
      getStore().dispatch(RequestTaskSchemaReduxActions.fetchSchemaSpec(schemaId));
    };

    const updateSchema = ({
      schemaId,
      schemaName,
      sqlIdentifier,
      spec,
    }: {
      schemaId: string;
      schemaName: string;
      sqlIdentifier: string | null;
      spec: SchemaSpec;
    }): void => {
      getStore().dispatch(RequestTaskSchemaReduxActions.updateSchemaSpec({schemaId, schemaName, spec}));
    };

    const {name, specString, errorState, sessionId} = this.reduxState().ui.schemaConfig;
    const {errors, errorMessage} = errorState != null ? errorState : {errors: null, errorMessage: null};
    return {
      allowEditMetadata: true,
      errors,
      errorMessage,
      name: getEditingName(name, requestTaskSchema),
      schema: requestTaskSchema,
      specString,
      fetchSchema,
      updateSchema,
      warehouseTableName: requestTaskSchema ? requestTaskSchema.sqlIdentifier : null,
      ownerName: requestTaskSchema ? requestTaskSchema.organizationJson.name : null,
      sessionId,
    };
  }
}

const RequestTaskSchemaEditSection = RequestTaskSchemaEditConnector.connect(
  SchemaEditSection
) as React.ComponentClass<SchemaPageEditSectionProps>;

export default function QueryingRequestTaskSchemaPage(props: SchemaPageProps): JSX.Element {
  const requestTaskSchema = useRequestTaskSchemaQuery(props.schemaId);
  if (requestTaskSchema == null) {
    return <CenteredHorizontalThrobber />;
  }

  const archiveConfig: ArchiveSchemaConfig = {
    onArchive: (id: string, purpose: string): void => {
      getStore().dispatch(bulkArchiveRequestTaskSchemas([id], purpose));
    },
    onUnarchive: (id: string): void => {
      getStore().dispatch(bulkUnarchiveRequestTaskSchemas([id]));
    },
    isArchivable: true,
    isUnarchivable: true,
  };

  return (
    <SchemaPage
      archiveConfig={archiveConfig}
      editSectionClass={RequestTaskSchemaEditSection}
      listingUrl={RequestTaskSchemaDAO.taskSchemaListingURL()}
      schemaId={props.schemaId}
      schema={requestTaskSchema}
      schemaType="REQUEST_TASK"
    />
  );
}
